<script setup>
import { computed, ref, watch } from 'vue';
import axios from 'axios';

const props = defineProps({
    userId: {
        type: [Number, String],
        required: true,
    },
	isAdmin: {
		type: [Number, String],
		default: 0,
	},
    initialPermissions: {
        type: Object,
        default: () => ({}),
    },
});

const permissionGroups = [
    // KOLUMNA 1
    {
        title: 'Kontrahenci',
        column: 1,
        permissions: [
            { key: 'canViewClients', label: 'Dostęp do kontrahentów' },
        ],
    },
    {
        title: 'Oferty',
        column: 1,
        permissions: [
            { key: 'canViewOffers', label: 'Dostęp do ofert' },
            { key: 'canSeeExtendedOfferView', label: 'Rozszerzony widok ofert' },
            { key: 'canEditOffers', label: 'Dostęp do tworzenia/edytowania ofert' },
            { key: 'canEditOffersTemplates', label: 'Dostęp do edytowania szablonów ofert' },
        ],
    },
    {
        title: 'Oferty Głowice',
        column: 1,
        permissions: [
            { key: 'canViewOffersGlowice', label: 'Dostęp do ofert głowic' },
            { key: 'canSeeExtendedOfferGlowiceView', label: 'Rozszerzony widok ofert głowic' },
            { key: 'canEditOffersGlowice', label: 'Dostęp do tworzenia/edytowania ofert głowic' },
            { key: 'canEditOffersGlowiceTemplates', label: 'Dostęp do edytowania szablonów ofert głowic' },
        ],
    },
    {
        title: 'Projekty',
        column: 1,
        permissions: [
            { key: 'canViewProjects', label: 'Dostęp do projektów' },
            { key: 'canViewProjectsGlowice', label: 'Dostęp do projektów głowic' },
            {
                key: 'canEditBasicProject',
                label: 'Podstawowa edycja projektów',
                description: 'Utwórz projekt, edycja nazwy, karta otwarcia zlecenia, zmiana priorytetów',
            },
            {
                key: 'canEditBasicProjectGlowice',
                label: 'Podstawowa edycja projektów głowic',
                description: 'Utwórz projekt, edycja nazwy, karta otwarcia zlecenia, zmiana priorytetów',
            },
            {
                key: 'canApproveProjects',
                label: 'Zatwierdzanie projektów',
                description: 'Możliwość zatwierdzania w projektach',
            },
            {
                key: 'canEditAdvancedProject',
                label: 'Rozszerzona edycja projektów',
                description: 'Projekt, kontakty projektu, wybrani dostawcy, ocena ryzyka, numeracja',
            },
            { key: 'canArchiveRestoreProjects', label: 'Archiwizowanie / przywracanie projektów' },
            { key: 'canEditTimeSpentAndBudgetProject', label: 'Edycja spędzonego czasu, budżetu/kosztów projektów' },
            { key: 'canViewPaymentsProject', label: 'Dostęp do płatności + status płatności projektów' },
            { key: 'canEditPaymentsProject', label: 'Edycja płatności + status płatności projektów' },
            { key: 'canEditPaymentsProjectGlowice', label: 'Edycja płatności + status płatności projektów głowic' },
            { key: 'canViewWarrantyProject', label: 'Dostęp do gwarancji projektów' },
            { key: 'canViewScheduleProject', label: 'Widok harmonogramu' },
            { key: 'canEditResourceSchedule', label: 'Edycja harmonogramu' },
            { key: 'canViewProjectInfo', label: 'Rozszerzony widok notatek' },
        ],
    },
    {
        title: 'Tablice zadań',
        column: 1,
        permissions: [
            { key: 'canAddKanbanCard', label: 'Dodawanie kart w tablicach' },
            { key: 'canArchiveKanbanCard', label: 'Archiwizowanie kart w tablicach' },
            { key: 'canViewActiveKanban', label: 'Widok aktywności w tablicach' },
        ],
    },

    // KOLUMNA 2
    {
        title: 'Zakupy',
        column: 2,
        permissions: [
            { key: 'canViewPurchases', label: 'Dostęp do zakupów' },
            { key: 'canViewArticlesPricesAndHistory', label: 'Lista artykułów - widoczność cen artykułów / historii' },
            { key: 'canEditArticles', label: 'Lista artykułów - tworzenie/edycja artykułów' },
            { key: 'canDelArticles', label: 'Lista artykułów - usuwanie artykułów' },
            { key: 'canEditArticlesTemplate', label: 'Lista artykułów - edycja szablonu artykułów' },
            { key: 'canRequiredProjectItemsViewOnly', label: 'Zapotrzebowanie - tylko podgląd' },
            { key: 'canEditRequiredProjectItems', label: 'Zapotrzebowanie - tworzenie/edycja pozycji' },
            { key: 'canUseInventorParser', label: 'Wczytaj z Inventora' },
            { key: 'canDelRequiredProjectItems', label: 'Zapotrzebowanie - usuwanie pozycji' },
            { key: 'canChangeStatusRequiredProjectItems', label: 'Zatwierdzanie zapotrzebowania projektowego' },
            { key: 'canViewQuoteRequests', label: 'Zapytania ofertowe - podgląd' },
            { key: 'canEditQuoteRequests', label: 'Zapytania ofertowe - tworzenie/edycja zapytań' },
            { key: 'canViewOrders', label: 'Zamówienia - podgląd' },
            { key: 'canChangeStatusApprovalOrders', label: 'Zamówienia w zatwierdzeniu - zmiana statusu mag.' },
            { key: 'canEditOrders', label: 'Zamówienia - tworzenie/edycja zamówień' },
            { key: 'canViewWarehouseMovements', label: 'Ruchy magazynowe - podgląd' },
            { key: 'canViewWarehouse', label: 'Stany magazynowe - podgląd' },
            { key: 'canWarehouseDispositions', label: 'Stany magazynowe - dyspozycja magazynowa' },
            { key: 'canViewOpisyFaktur', label: 'Opisy faktur' },
            { key: 'canCreatorSettings', label: 'Ustawienia kreatora' },
            { key: 'canZakupySettings', label: 'Ustawienia' },
            { key: 'canZakupyStats', label: 'Statystyki zakupów - dostęp' },
        ],
    },
    {
        title: 'Kadry / Urlopy',
        column: 2,
        permissions: [
            { key: 'canViewKadry', label: 'Dostęp do kadr' },
            { key: 'canViewUrlopyDzial', label: 'Urlopy pracowników działu' },
            { key: 'canViewUrlopy', label: 'Urlopy wszystkich pracowników' },
            { key: 'canAddUrlop', label: 'Dodawanie wniosków' },
            { key: 'canAcceptUrlop', label: 'Zatwierdzanie wniosków' },
            { key: 'canRejectUrlop', label: 'Odrzucanie wniosków' },
            { key: 'canEditUrlop', label: 'Edycja wniosków' },
            { key: 'canDelUrlop', label: 'Usuwanie wniosków' },
            { key: 'canAddL4', label: 'Dodawanie zwolnień L4' },
            { key: 'canViewWorkersDane', label: 'Lista i dane pracowników' },
            { key: 'canEditKadry', label: 'Kadry - edycja ustawień' },
            { key: 'canViewSMBUsers', label: 'Dostęp do dysków' },
            { key: 'canViewERPperms', label: 'Przyznane uprawnienia ERP' },
        ],
    },
    {
        title: 'Księgowość',
        column: 1,
        permissions: [
            { key: 'canViewKsiegowosc', label: 'Dostęp do księgowości' },
        ],
    },
    {
        title: 'BHP',
        column: 1,
        permissions: [
            { key: 'canViewBHP', label: 'Dostęp do BHP' },
            { key: 'canEditBHP', label: 'Dodawanie/edycja danych' },
            { key: 'canDelDataBHP', label: 'Usuwanie danych' },
        ],
    },
    {
        title: 'Odzież ochronna',
        column: 2,
        permissions: [
            { key: 'canViewUbrania', label: 'Dostęp do odzieży' },
            { key: 'canEditUbrania', label: 'Dodawanie/edycja odzieży' },
            { key: 'canDelUbrania', label: 'Usuwanie odzieży' },
        ],
    },

    // KOLUMNA 3
    {
        title: 'Serwis',
        column: 3,
        permissions: [
            { key: 'canViewService', label: 'Dostęp do serwisu' },
            { key: 'canViewClientPrice', label: 'Widoczność kwoty zgłoszenia' },
            { key: 'canViewServiceCosts', label: 'Edycja kosztów zgłoszenia' },
            { key: 'canViewServiceFiles', label: 'Dostęp do plików zgłoszenia' },
            { key: 'canServicePDF', label: 'Dostęp do plików PDF' },
            { key: 'canAddService', label: 'Dodawanie zgłoszeń' },
            { key: 'canEditService', label: 'Edycja zgłoszeń' },
            { key: 'canChangeServiceStatus', label: 'Zmiana statusu zgłoszenia' },
            { key: 'canDelService', label: 'Usuwanie zgłoszeń' },
            { key: 'canViewRate', label: 'Dostęp do stawek serwisowych' },
            { key: 'canAddEditRate', label: 'Dodawanie/edycja stawek serwisowych' },
            { key: 'canDelRate', label: 'Usuwanie stawek serwisowych' },
            { key: 'canViewServiceCategory', label: 'Dostęp do kategorii serwisowych' },
            { key: 'canAddServiceCategory', label: 'Dodawanie kategorii serwisowych' },
            { key: 'canEditServiceCategory', label: 'Edycja kategorii serwisowych' },
            { key: 'canDelServiceCategory', label: 'Usuwanie kategorii serwisowych' },
            { key: 'canViewSerwisZestawienia', label: 'Dostęp do zestawień' },
        ],
    },
    {
        title: 'Pracownik',
        column: 3,
        permissions: [
            { key: 'canViewCardRead', label: 'Widok odbić karty' },
            { key: 'canViewWorktime', label: 'Dostęp do czasu pracy' },
            { key: 'canAddWorktime', label: 'Dodawanie czasu pracy' },
            { key: 'canViewWorktimes', label: 'Zestawienia czasu pracy' },
        ],
    },
    {
        title: 'Salki konferencyjne',
        column: 3,
        permissions: [
            { key: 'canViewConferenceRoom', label: 'Dostęp do harmonogramu' },
            { key: 'canAddConferenceRoom', label: 'Dodawanie wpisów do harmonogramu' },
            { key: 'canAddConferenceRooms', label: 'Dodawanie salek' },
        ],
    },
    {
        title: 'Produkcja',
        column: 3,
        permissions: [
            { key: 'canViewProdukcja', label: 'Dostęp do produkcji' },
            { key: 'canViewProdukcjaZewn', label: 'Dostęp do produkcji zewnętrznej' },
            { key: 'canViewCheckProdukcja', label: 'Dostęp do statusu produkcji' },
            { key: 'canViewMaszyny', label: 'Dostęp do produkcji maszyny' },
            { key: 'canEditMaszyny', label: 'Edycja kW/h w Maszyny' },
            { key: 'canAddProdukcjaOut', label: 'Zamówienia zewnętrzne - dodawanie' },
            { key: 'canDelProdukcjaOut', label: 'Zamówienia zewnętrzne - usuwanie' },
            { key: 'canEditProdukcjaOut', label: 'Zamówienia zewnętrzne - edycja' },
            { key: 'canAddOutProducent', label: 'Kontrahenci - dodawanie/edycja' },
            { key: 'canDelOutProducent', label: 'Kontrahenci - usuwanie' },
            { key: 'canAddproductionOutStatus', label: 'Statusy - dodawanie/edycja' },
            { key: 'canDelproductionOutStatus', label: 'Statusy - usuwanie' },
        ],
    },
];

const permissions = ref({ ...props.initialPermissions });
const saving = ref(false);
const saveError = ref('');

watch(
    () => props.initialPermissions,
    (value) => {
        permissions.value = { ...value };
    },
    { deep: true }
);

const columns = computed(() => [
    permissionGroups.filter((group) => group.column === 1),
    permissionGroups.filter((group) => group.column === 2),
    permissionGroups.filter((group) => group.column === 3),
]);

async function updatePermissions(key, checked) {
    const previousValue = permissions.value[key];

    permissions.value[key] = checked;
    saving.value = true;
    saveError.value = '';

    try {
        await axios.post(
            route('user.updatePermissions', { user: props.userId }),
            {
                permissions: permissions.value,
            }
        );
    } catch (error) {
        permissions.value[key] = previousValue;
        saveError.value = 'Nie udało się zapisać uprawnień. Spróbuj ponownie.';
        console.error('Błąd zapisu uprawnień:', error);
    } finally {
        saving.value = false;
    }
}
</script>

<template>
    <div class="row mt-5 permissions-layout">
        <div
            v-for="(column, columnIndex) in columns"
            :key="columnIndex"
            class="col-md-4 col-sm-12 permissions-column"
            :class="{
                'permissions-column-divider': columnIndex > 0
            }"
        >
            <div class="permissions-column-inner">
                <section
                    v-for="group in column"
                    :key="group.title"
                    class="permission-group mb-4"
                >
                    <h5 class="permission-group-title mb-3">
                        {{ group.title }}
                    </h5>

                    <table class="w-100">
                        <tbody>
                            <tr
                                v-for="permission in group.permissions"
                                :key="permission.key"
                                class="permission-row"
                            >
                                <td class="py-2 pe-3">
                                    <label
                                        class="permission-label"
                                        :for="permission.key"
                                    >
                                        {{ permission.label }}

                                        <br v-if="permission.description">

                                        <small
                                            v-if="permission.description"
                                            class="permission-description"
                                        >
                                            ({{ permission.description }})
                                        </small>
                                    </label>
                                </td>

                                <td class="text-end align-middle">
                                    <label
                                        class="switch s-icons s-outline s-outline-success mb-0"
                                    >
                                        <input
                                            :id="permission.key"
                                            type="checkbox"
                                            :checked="Boolean(permissions[permission.key])"
                                            :disabled="saving || Number(props.isAdmin) !== 1"
                                            @change="
                                                updatePermissions(
                                                    permission.key,
                                                    $event.target.checked
                                                )
                                            "
                                        >
                                        <span class="slider round"></span>
                                    </label>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </section>
            </div>
        </div>

        <div
            v-if="saveError"
            class="col-12 mt-3"
        >
            <div class="alert alert-danger">
                {{ saveError }}
            </div>
        </div>
    </div>
</template>

<style scoped>
.permissions-layout {
    row-gap: 24px;
}

.permissions-column {
    padding: 0 20px;
}

.permissions-column-divider {
    border-left: 1px solid #dee2e6;
}

.permissions-column-inner {
    height: 100%;
    padding: 16px;
    border-radius: 8px;
    background: rgba(128, 128, 128, 0.035);
}

.permissions-column-heading {
    margin-bottom: 24px;
    padding-bottom: 12px;
    border-bottom: 2px solid #dee2e6;
    font-size: 13px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.7px;
    color: #6c757d;
}

.permission-group {
    padding-bottom: 16px;
    border-bottom: 1px solid rgba(128, 128, 128, 0.15);
}

.permission-group:last-child {
    border-bottom: none;
    padding-bottom: 0;
}

.permission-group-title {
    padding-left: 10px;
    border-left: 3px solid #198754;
    font-weight: 600;
}

.permission-label {
    display: inline-block;
    cursor: pointer;
    margin-bottom: 0;
}

.permission-description {
    color: #6c757d;
}

.permission-row {
    border-bottom: 1px solid rgba(128, 128, 128, 0.08);
}

.permission-row:last-child {
    border-bottom: none;
}

@media (max-width: 767.98px) {
    .permissions-column {
        padding: 0 12px;
    }

    .permissions-column-divider {
        border-left: none;
        border-top: 2px solid #dee2e6;
        padding-top: 20px;
    }
}
</style>

